/**
 * <XtsInfoBars> — Renders InfoBarConfig[].
 *
 * Like Stations but per-XPU/Part with custom range, color, thickness and
 * displacement. Z-stacking via zIndex (multiplied with a small Z step).
 *
 * `partStartPositionMm`, `partEndPositionMm`, and each marker's
 * `positionMm` are interpreted in the user's frame (subject to the
 * XPU's `positionFrame`) — same convention as Stations / Areas /
 * Movers, so an InfoBar that visually sits "at the 250 mm tick" matches
 * a mover whose `partPositionMm` is 250.
 */

import React from 'react';
import type { BuiltChain } from '../../geometry/ChainBuilder.js';
import {
  sampleChainPoint,
  sampleChainRange,
} from '../../geometry/sampleChain.js';
import { userToChainPositionMm } from '../../geometry/positionFrame.js';
import { PathTube } from './PathTube.js';
import { PathMarker } from './PathMarker.js';
import { PartTransformGroup } from './PartTransformGroup.js';
import type {
  InfoBarConfig,
  PartTransformation,
  PositionFrame,
} from '../../types.js';

interface XtsInfoBarsProps {
  /** Already filtered to bars whose XPU we're rendering. */
  infoBars: InfoBarConfig[];
  /** Map of part `objectId` (within XPU) → chain. */
  chainsByPartObjectId: Map<number, BuiltChain>;
  /** Per-part transforms keyed by `PartConfig.objectId`. */
  partTransformsByPartObjectId?: Map<number, PartTransformation | undefined>;
  /** XPU position frame applied to start / end / marker positions. */
  positionFrame?: PositionFrame;
}

const Z_STEP_PER_INDEX = 4;

export const XtsInfoBars: React.FC<XtsInfoBarsProps> = ({
  infoBars,
  chainsByPartObjectId,
  partTransformsByPartObjectId,
  positionFrame,
}) => {
  return (
    <group name="xts-infobars">
      {infoBars
        .filter((b) => b.visible)
        .map((bar, i) => {
          const chain = chainsByPartObjectId.get(bar.partObjectId);
          if (!chain) return null;
          const partTransform = partTransformsByPartObjectId?.get(
            bar.partObjectId,
          );
          const liftZ = bar.displacement + bar.zIndex * Z_STEP_PER_INDEX;
          const startChain = userToChainPositionMm(
            bar.partStartPositionMm,
            positionFrame,
          );
          const endChain = userToChainPositionMm(
            bar.partEndPositionMm,
            positionFrame,
          );
          const points = sampleChainRange(
            chain,
            startChain,
            endChain,
            15,
            liftZ,
          );
          return (
            <PartTransformGroup
              key={i}
              name={`infobar-${i}`}
              partTransformation={partTransform}
            >
              <PathTube
                points={points}
                thicknessMm={Math.max(bar.thickness, 1)}
                color={bar.color.color}
              />
              {(bar.markers ?? []).map((m, j) => {
                const markerChainPos = userToChainPositionMm(
                  m.positionMm,
                  positionFrame,
                );
                const sample = sampleChainPoint(
                  chain,
                  markerChainPos,
                  liftZ + 2,
                );
                if (!sample) return null;
                return (
                  <PathMarker
                    key={j}
                    position={sample.position}
                    tangent={sample.tangent}
                    shape={m.shape}
                    sizeMm={m.sizeMm}
                    color={m.color ?? bar.color.color}
                  />
                );
              })}
            </PartTransformGroup>
          );
        })}
    </group>
  );
};
